import {
  collection,
  query,
  where,
  orderBy,
  limit,	
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  doc,
  writeBatch,
  Timestamp
} from "firebase/firestore";
import { db } from "../firebase";

const SYSTEM_START_DATE = "2026-01-10";

/* ================= IST DATE UTIL ================= */

export function getISTDateString(date = new Date()) {
  

  const istOffset = 5.5 * 60 * 60 * 1000;
  const ist = new Date(date.getTime() + istOffset);
  return ist.toISOString().slice(0, 10); // YYYY-MM-DD
}

/**
 * Converts a YYYY-MM-DD date string into dd-mm-yyyy format.
 * Used to match the document IDs in the hawker_cdm collection.
 */
export function formatDateToDDMMYYYY(dateStr) {
  if (!dateStr || dateStr.length !== 10) return dateStr;
  const [y, m, d] = dateStr.split("-");
  return `${d}-${m}-${y}`;
}

/* ================= PARTY LIST ================= */

export async function fetchParties(partytype = "hawker", clientId = null) {
  let q;
  if (partytype === "all") {
    q = query(
      collection(db, "partyMaster")
    );
  } else {
    q = query(
      collection(db, "partyMaster"),
      where("partytype", "==", partytype)
    );
  }

  if (clientId) {
    q = query(q, where("clientId", "==", clientId));
  }

  const snap = await getDocs(q);
  return snap.docs.map(d => ({
    id: d.id,
    partyId: d.data().partyId,
    name: d.data().name,
    payment_mode: d.data().payment_mode || "Cash",
    partytype: d.data().partytype || "hawker"
  }));
}

/* ================= PREVIOUS DAY LEDGER ================= */

export async function fetchPreviousDayLedger(partyId, todayIST) {
  // ✅ Day-1 system start: do NOT block
  
  
 if (todayIST <= SYSTEM_START_DATE) {
  return { closingBalance: 0 };
}


  const prev = new Date(todayIST);
  prev.setDate(prev.getDate() - 1);
 


const q = query(
  collection(db, "salesman_ledger"),
  
  
  
  
  where("partyId", "==", partyId),
  
  
  
  where("dateIST", "<", todayIST),
  orderBy("dateIST", "desc"),
  limit(1)
);
  const snap = await getDocs(q);
  return snap.empty ? null : snap.docs[0].data();
}

/* ================= TODAY LEDGER ================= */
export async function fetchTodayLedger(partyId, todayIST) {
  const ref = doc(db, "salesman_ledger", `${partyId}_${todayIST}`);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}

/* ================= TODAY ORDER AMOUNT ================= */

export async function fetchTodayOrderAmount(partyId, todayIST, paymentMode = "Cash") {
  const cleanPartyId = (partyId || "").trim(); // 🚩 Safety: Handle potential spaces

  if (paymentMode === "Credit" || paymentMode === "Hawker") {
    // 🚩 CREDIT LOGIC: Fetch all APPROVED past orders that haven't been synced to a ledger yet
    const q = query(
      collection(db, "orders"),
      where("partyId", "==", cleanPartyId),
      where("status", "==", "APPROVED"),
      where("ledgerSynced", "==", false),
      where("approvedDateOnly", "<", todayIST)
      // 🚩 Removed redundant mode check to ensure no "former Cash" bills are missed
    );
    const snap = await getDocs(q);
    let total = 0;
    const orderIds = [];
    snap.forEach(d => {
      total += Number(d.data().totalAmount || 0);
      orderIds.push(d.id);
    });
    return { total, orderIds };
  } else {
    // 🚩 CASH LOGIC: Fetch only today's approved orders
    const q = query(
      collection(db, "orders"),
      where("partyId", "==", cleanPartyId),
      where("approvedDateOnly", "==", todayIST),
      where("status", "==", "APPROVED")
    );
    const snap = await getDocs(q);
    let total = 0;
    const orderIds = [];
    snap.forEach(d => {
      total += Number(d.data().totalAmount || 0);
      orderIds.push(d.id);
    });
    return { total, orderIds };
  }
}

/* ================= TODAY HAWKER CDM ================= */

export async function fetchTodayHawkerCDM(partyId, todayIST) {
  const formattedDate = formatDateToDDMMYYYY(todayIST);
  const ref = doc(db, "hawker_cdm", `${partyId}_${formattedDate}`);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    const data = snap.data();
    return {
      cdmAmount: Number(data.cdm || 0),
      nicheBrandAmount: Number(data.nicheBrands || 0)
    };
  }
  return { cdmAmount: 0, nicheBrandAmount: 0 };
}

/* ================= UPDATE ORDER SYNC STATUS ================= */

export async function updateOrderSyncStatus(orderIds, isSynced) {
  if (!orderIds || orderIds.length === 0) {
    console.warn("updateOrderSyncStatus: No order IDs provided.");
    return;
  }
  
  console.log(`Processing ${isSynced ? 'Sync' : 'Un-Sync'} for ${orderIds.length} root IDs...`);
  const ops = [];

  for (const id of orderIds) {
    // 🚩 Robust: set with merge ensures update even if doc state is slightly odd
    ops.push({ 
      ref: doc(db, "orders", id), 
      data: { ledgerSynced: isSynced }, 
      options: { merge: true } 
    });

    // Handle split records
    try {
        const qOutlets = query(collection(db, "outletOrders"), where("rootOrderId", "==", id));
        const outletSnap = await getDocs(qOutlets);
        outletSnap.forEach(d => {
          ops.push({ 
            ref: doc(db, "outletOrders", d.id), 
            data: { ledgerSynced: isSynced }, 
            options: { merge: true } 
          });
        });
    } catch (err) {
        console.error(`Failed to trigger sub-updates for rootId: ${id}`, err);
    }
  }

  // 🚀 Execute CHUNKED Commits (Firestore 500-limit safety)
  let batch = writeBatch(db);
  let count = 0;
  for (const op of ops) {
    batch.set(op.ref, op.data, op.options);
    count++;
    if (count >= 450) {
      await batch.commit();
      batch = writeBatch(db);
      count = 0;
    }
  }

  if (count > 0) {
    await batch.commit();
  }
  
  console.log(`Successfully completed ${isSynced ? 'sync' : 'un-sync'} operations across ${ops.length} total writes.`);
}

/* ================= FETCH ONLINE COLLECTION FROM SHEETS ================= */

export async function fetchOnlineCollectionMap(dateIST) {
  try {
    // 1. Fetch Dynamic URL from Settings (Absolute Source of Truth)
    const settingsSnap = await getDoc(doc(db, "settings", "google_sheets"));
    const settings = settingsSnap.exists() ? settingsSnap.data() : {};
    const baseUrl = settings.onlineCollectionUrl;

    if (!baseUrl) {
      throw new Error("Online Collection Bridge not configured in System Settings.");
    }

    const res = await fetch(`${baseUrl}?date=${dateIST}`);
    if (!res.ok) return {};
    return await res.json();
  } catch (err) {
    console.error("Online Sync Error:", err.message);
    return {};
  }
}


/* ================= SAVE LEDGER ================= */

export async function saveDayClose({
  partyId,
  openingBalance,
  paymentMode,
  denominations
}) {
  const dateIST = getISTDateString();

  const cashCollected =
    (denominations[500] || 0) * 500 +
    (denominations[200] || 0) * 200 +
    (denominations[100] || 0) * 100 +
    (denominations[50] || 0) * 50 +
    (denominations[20] || 0) * 20 +
    (denominations[10] || 0) * 10 +
    (denominations[5] || 0) * 5 +
    (denominations[1] || 0) * 1;

  // 🚩 Step 1: Robust Party Lookup using Field Queries (Not Document ID)
  const cleanId = (partyId || "").trim();
  const partyRef = collection(db, "partyMaster");
  
  // Search for the exact ID first
  let pQuery = query(partyRef, where("partyId", "==", cleanId));
  let pSnap = await getDocs(pQuery);
  
  // Try uppercase fallback if direct match fails
  if (pSnap.empty) {
    pQuery = query(partyRef, where("partyId", "==", cleanId.toUpperCase()));
    pSnap = await getDocs(pQuery);
  }

  const trueMode = !pSnap.empty ? pSnap.docs[0].data().payment_mode : "Cash";
  
  // 🚩 DIAGNOSTIC: Log resolved mode for troubleshooting
  console.log(`[DEBUG] saveDayClose: partyId=${cleanId}, resolvedMode=${trueMode}`);

  const ref = doc(db, "salesman_ledger", `${cleanId}_${dateIST}`); // 🚩 Use cleanId here!
  const existingSnap = await getDoc(ref);

  // FETCH FRESH ONLINE DATA FROM GOOGLE SHEETS
  const onlineMap = await fetchOnlineCollectionMap(dateIST);
  const freshOnlineAmount = Number(onlineMap[partyId] || 0);

  // ===========================
  // CASE 1 — NO ENTRY
  // ===========================
  if (!existingSnap.exists()) {
    // If no entry exists, fetch fresh order amount using the TRUE mode and cleanId
    const { total: freshOrderAmount, orderIds } = await fetchTodayOrderAmount(cleanId, dateIST, trueMode);
    
    const safeOpeningBalance = Number(openingBalance) || 0;
    const { cdmAmount, nicheBrandAmount } = await fetchTodayHawkerCDM(cleanId, dateIST);

    const closingBalance = safeOpeningBalance + freshOrderAmount + cdmAmount + nicheBrandAmount - freshOnlineAmount - cashCollected;

    await setDoc(ref, {
      partyId: cleanId, // 🚩 Use cleanId here!
      dateIST,
      openingBalance: safeOpeningBalance,
      orderAmount: freshOrderAmount,
      consumedOrderIds: orderIds, // Save the IDs we've settled
      cdmAmount,
      nicheBrandAmount,
      onlineCollected: freshOnlineAmount,
      denominations,
      cashCollected,
      closingBalance,
      status: "SUBMITTED",
      dayClose: false,
      createdAt: Timestamp.now()
    });

    // MARK ORDERS AS SYNCED (so they don't show up again)
    await updateOrderSyncStatus(orderIds, true);
    await cascadeLedgerBalance(cleanId, dateIST, closingBalance);
    return;
  }

  // ===========================
  // CASE 2 — ENTRY EXISTS
  // ===========================
  const existingData = existingSnap.data();

  // 🚩 Strict Submission Lock
  // Prevent double submissions. If corrections are needed, they MUST go through the Cashier Rejection flow.
  if (existingData.status === "APPROVED" || existingData.status === "SUBMITTED") {
    throw new Error(`This collection has already been ${existingData.status}. To make corrections, the Cashier must reject it first.`);
  }

  const safeOpeningBalance = Number(existingData.openingBalance) || 0;
  
  // For entries that already exist (e.g. from Cloud Function), we RE-FETCH the orders 
  // to pick up anything newly approved since the CF ran.
  const { total: freshOrderAmount, orderIds } = await fetchTodayOrderAmount(cleanId, dateIST, trueMode);
  
  const { cdmAmount, nicheBrandAmount } = await fetchTodayHawkerCDM(cleanId, dateIST);
  const closingBalance = safeOpeningBalance + freshOrderAmount + cdmAmount + nicheBrandAmount - freshOnlineAmount - cashCollected;

  await setDoc(ref, {
    partyId: cleanId, // 🚩 Use cleanId here!
    dateIST,
    openingBalance: safeOpeningBalance,
    orderAmount: freshOrderAmount,         
    consumedOrderIds: orderIds,              // Update with newest IDs
    cdmAmount,                               
    nicheBrandAmount,                        
    onlineCollected: freshOnlineAmount,      
    denominations,                           
    cashCollected,                           
    closingBalance,                          
    status: "SUBMITTED",        
    dayClose: false,
    rejectionRemark: "",      
    updatedAt: Timestamp.now()
  });

  // MARK ORDERS AS SYNCED
  await updateOrderSyncStatus(orderIds, true);
  await cascadeLedgerBalance(cleanId, dateIST, closingBalance);
}


/* ================= SYNC LEDGER ORDER AMOUNT ================= */
// Call this whenever an order is APPROVED
export async function syncLedgerOrderAmount(partyId, dateIST) {
  // 1. Get party mode
  const pSnap = await getDoc(doc(db, "partyMaster", partyId));
  const mode = pSnap.exists() ? pSnap.data().payment_mode : "Cash";

  // 2. Freshly recount orders with the new logic
  const { total: freshOrderAmount, orderIds } = await fetchTodayOrderAmount(partyId, dateIST, mode);

  const ref = doc(db, "salesman_ledger", `${partyId}_${dateIST}`);
  const existingSnap = await getDoc(ref);

  if (existingSnap.exists()) {
    const existingData = existingSnap.data();

    // 🚩 CRITICAL FIX: Lock Protection Layer
    // Do not wipe out orderAmount or consumedOrderIds if the ledger is already checked in.
    if (existingData.status === "SUBMITTED" || existingData.status === "APPROVED") {
        console.log(`[DEBUG] syncLedgerOrderAmount: Ledger for ${partyId} is ${existingData.status}. Skipping overwrite to protect tracked data.`);
        return;
    }

    const safeOpeningBalance = Number(existingData.openingBalance) || 0;
    const currentOnlineCollected = Number(existingData.onlineCollected) || 0;
    const currentCashCollected = Number(existingData.cashCollected) || 0;
    const { cdmAmount, nicheBrandAmount } = await fetchTodayHawkerCDM(partyId, dateIST);

    const closingBalance = safeOpeningBalance + freshOrderAmount + cdmAmount + nicheBrandAmount - currentOnlineCollected - currentCashCollected;

    await setDoc(ref, {
      orderAmount: freshOrderAmount,
      consumedOrderIds: orderIds,
      cdmAmount,
      nicheBrandAmount,
      closingBalance: closingBalance,
      updatedAt: Timestamp.now()
    }, { merge: true });
    
  } else {
    const prevLedger = await fetchPreviousDayLedger(partyId, dateIST);
    const safeOpeningBalance = prevLedger ? Number(prevLedger.closingBalance || 0) : 0;
    const { cdmAmount, nicheBrandAmount } = await fetchTodayHawkerCDM(partyId, dateIST);

    const closingBalance = safeOpeningBalance + freshOrderAmount + cdmAmount + nicheBrandAmount;

    await setDoc(ref, {
      partyId,
      dateIST,
      openingBalance: safeOpeningBalance,
      orderAmount: freshOrderAmount,
      consumedOrderIds: orderIds,
      cdmAmount,
      nicheBrandAmount,
      onlineCollected: 0,
      cashCollected: 0,
      closingBalance,
      denominations: {},
      status: "PENDING",
      dayClose: false,
      createdAt: Timestamp.now()
    });
  }
}

/* ================= CASCADE LEDGER BALANCE ================= */
export async function cascadeLedgerBalance(partyId, fromDateIST, newClosingBalance) {
    const todayIST = getISTDateString();
    
    // We want to pass the new expected opening balance to the next day
    let propagatedOpening = newClosingBalance;
    
    let currentDate = new Date(fromDateIST);
    let iter = 0;
    while(iter < 10) { 
        currentDate.setDate(currentDate.getDate() + 1);
        const nextDateStr = currentDate.toISOString().slice(0, 10);
        
        if (nextDateStr > todayIST) break;
        
        const nextRef = doc(db, "salesman_ledger", `${partyId}_${nextDateStr}`);
        const nextSnap = await getDoc(nextRef);
        if (nextSnap.exists()) {
            const nextData = nextSnap.data();
            const oldOpening = Number(nextData.openingBalance || 0);
            const oldClosing = Number(nextData.closingBalance || 0);
            
            const delta = propagatedOpening - oldOpening;
            if (delta === 0) break; // If no delta, chain is fully matched, stop checking
            
            const newlyCalculatedClosing = oldClosing + delta;
            console.log(`[CASCADE] ${partyId} for ${nextDateStr}: Adjusting by ${delta}. New Closing: ${newlyCalculatedClosing}`);
            
            await setDoc(nextRef, {
                openingBalance: propagatedOpening,
                closingBalance: newlyCalculatedClosing,
                updatedAt: Timestamp.now()
            }, { merge: true });
            
            // ✅ SYNC TO PARTY MASTER if this is the "latest" (today's) ledger record
            if (nextDateStr === todayIST) {
              await syncPartyMasterOpeningBalance(partyId, propagatedOpening);
            }

            propagatedOpening = newlyCalculatedClosing; // pass it to the next day
        } else {
            // Next day doesn't exist, stop cascading
            break;
        }
        iter++;
    }
}

/* ================= SYNC PARTY MASTER OPENING BALANCE ================= */
export async function syncPartyMasterOpeningBalance(partyId, openingBalance) {
  if (!partyId) return;
  try {
    const cleanId = partyId.trim();
    const ref = doc(db, "partyMaster", cleanId);
    await updateDoc(ref, {
      opening_balance: Number(openingBalance || 0)
    });
    console.log(`[SYNC] Updated PartyMaster ${cleanId} opening_balance to ${openingBalance}`);
  } catch (err) {
    console.error(`[SYNC ERROR] Failed to update PartyMaster ${partyId}:`, err);
  }
}
